/**
 * Currency and locale allowlists, mirroring `backend/app/core/locale_format.py`.
 *
 * The backend is authoritative: it validates on write. This list exists so the
 * UI can offer exactly the same choices and never render a value the API would
 * reject. Keep both files in sync.
 *
 * Every currency has exactly 2 minor decimal digits, so millesimal and money
 * formatting never need per-currency scale handling.
 */

export const SUPPORTED_CURRENCIES = [
  'EUR',
  'GBP',
  'USD',
  'CHF',
  'SEK',
  'NOK',
  'DKK',
  'PLN',
  'CZK',
  'RON',
  'BGN',
  'TRY',
] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

export const SUPPORTED_LOCALES = [
  'el-GR',
  'en-GB',
  'en-US',
  'en-IE',
  'de-DE',
  'fr-FR',
  'es-ES',
  'it-IT',
  'nl-NL',
  'pl-PL',
  'ro-RO',
  'bg-BG',
  'tr-TR',
] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const SUPPORTED_LANGUAGES = ['en', 'el'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/**
 * localStorage cache key for the UI language.
 *
 * Lives in this dependency-free module on purpose: `auth-context` needs it to
 * seed the language from `users.preferred_language`, and importing it from the
 * `use-locale` hook would create the cycle
 * auth-context -> use-locale -> property-context -> auth-context, which
 * breaks `PropertyProvider` at runtime.
 */
export const LANGUAGE_STORAGE_KEY = 'koinon_language';

export const DEFAULT_CURRENCY = 'EUR';
// English by default: en-GB keeps the unambiguous dd/MM date order that
// financial due dates need. el-GR stays fully supported, just not the default.
export const DEFAULT_LOCALE = 'en-GB';

/** Human-readable currency names for the settings selector. */
export const CURRENCY_LABELS: Record<SupportedCurrency, string> = {
  EUR: 'Euro',
  GBP: 'British Pound',
  USD: 'US Dollar',
  CHF: 'Swiss Franc',
  SEK: 'Swedish Krona',
  NOK: 'Norwegian Krone',
  DKK: 'Danish Krone',
  PLN: 'Polish Zloty',
  CZK: 'Czech Koruna',
  RON: 'Romanian Leu',
  BGN: 'Bulgarian Lev',
  TRY: 'Turkish Lira',
};

/** BCP-47 tags Intl always understands; labels are the endonym. */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  'el-GR': 'Ελληνικά (Ελλάδα)',
  'en-GB': 'English (United Kingdom)',
  'en-US': 'English (United States)',
  'en-IE': 'English (Ireland)',
  'de-DE': 'Deutsch (Deutschland)',
  'fr-FR': 'Français (France)',
  'es-ES': 'Español (España)',
  'it-IT': 'Italiano (Italia)',
  'nl-NL': 'Nederlands (Nederland)',
  'pl-PL': 'Polski (Polska)',
  'ro-RO': 'Română (România)',
  'bg-BG': 'Български (България)',
  'tr-TR': 'Türkçe (Türkiye)',
};

const CURRENCY_SET: ReadonlySet<string> = new Set(SUPPORTED_CURRENCIES);
const LOCALE_SET: ReadonlySet<string> = new Set(SUPPORTED_LOCALES);

export function isSupportedCurrency(value: string | null | undefined): value is SupportedCurrency {
  if (!value) return false;
  return CURRENCY_SET.has(value.trim().toUpperCase());
}

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  if (!value) return false;
  return LOCALE_SET.has(value.trim());
}

export function normalizeCurrency(value: string | null | undefined): SupportedCurrency {
  if (!isSupportedCurrency(value)) return DEFAULT_CURRENCY;
  return (value as string).trim().toUpperCase() as SupportedCurrency;
}

export function normalizeLocale(value: string | null | undefined): SupportedLocale {
  if (!isSupportedLocale(value)) return DEFAULT_LOCALE;
  return (value as string).trim() as SupportedLocale;
}
